import type { TablePaginationConfig } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useListQueryState,
  type ListSchema,
  type ListValues
} from './useListQueryState'

/**
 * URL-synced table state preset: builds on top of `useListQueryState` with
 * `page`, `per_page`, `search`, `ordering` already wired up, plus AntD-Table
 * glue (pagination config + onChange handler that translates the sorter into
 * DRF-style ordering strings — e.g. `created_at` / `-created_at`).
 *
 * Pass `extraSchema` to add feature-specific filters (status, role, etc.).
 */

export const DEFAULT_PER_PAGE = 25
export const DEFAULT_PER_PAGE_OPTIONS: readonly number[] = [
  25, 50, 100, 175, 250
]

type BaseSchema = {
  page: { type: 'number'; default: number }
  per_page: { type: 'number'; default: number; allowed: readonly number[] }
  search: { type: 'string'; default: string }
  ordering: { type: 'string'; default: string }
}

type Options = {
  defaultPerPage?: number
  perPageOptions?: readonly number[]
}

const buildBaseSchema = (
  defaultPerPage: number,
  allowed: readonly number[]
): BaseSchema => ({
  page: { type: 'number', default: 1 },
  per_page: { type: 'number', default: defaultPerPage, allowed },
  search: { type: 'string', default: '' },
  ordering: { type: 'string', default: '' }
})

const sorterToOrdering = <T>(
  sorter: SorterResult<T> | SorterResult<T>[]
): string => {
  const first = Array.isArray(sorter) ? sorter[0] : sorter
  if (!first?.order || !first.field) return ''
  // AntD uses dot/array paths for nested dataIndex — DRF only takes a single
  // field key, so we use the column `key` when available, else the last
  // segment of `field`.
  const fieldRaw = (first.columnKey ?? first.field) as string | string[]
  const field = Array.isArray(fieldRaw)
    ? fieldRaw[fieldRaw.length - 1]
    : fieldRaw

  return first.order === 'descend' ? `-${field}` : String(field)
}

export const orderingToSort = (
  ordering: string
): { field: string; order: 'ascend' | 'descend' } | null => {
  if (!ordering) return null

  return ordering.startsWith('-')
    ? { field: ordering.slice(1), order: 'descend' }
    : { field: ordering, order: 'ascend' }
}

export const useTableQueryState = <const Extra extends ListSchema>(
  extraSchema: Extra = {} as Extra,
  options: Options = {}
) => {
  const { t } = useTranslation()

  const perPageOptions = options.perPageOptions ?? DEFAULT_PER_PAGE_OPTIONS
  const defaultPerPage = options.defaultPerPage ?? DEFAULT_PER_PAGE

  const schema = useMemo(
    () =>
      ({
        ...buildBaseSchema(defaultPerPage, perPageOptions),
        ...extraSchema
      }) as BaseSchema & Extra,
    [defaultPerPage, perPageOptions, extraSchema]
  )

  const state = useListQueryState(schema)

  const buildPagination = useCallback(
    (total?: number): TablePaginationConfig => ({
      current: state.values.page,
      pageSize: state.values.per_page,
      total: total ?? 0,
      pageSizeOptions: perPageOptions.map(String),
      showSizeChanger: { showSearch: false },
      size: 'small',
      hideOnSinglePage: false,
      showTotal: (count, [from, to]) =>
        t('pagination.range', {
          from,
          to,
          total: count,
          defaultValue: `${from}-${to} of ${count}`
        })
    }),
    [perPageOptions, state.values.page, state.values.per_page, t]
  )

  const onTableChange = useCallback(
    <T>(
      pagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter: SorterResult<T> | SorterResult<T>[]
    ) => {
      const patch: Record<string, unknown> = {}
      const nextOrdering = sorterToOrdering(sorter)
      if (nextOrdering !== state.values.ordering) {
        patch.ordering = nextOrdering
      }
      if (
        pagination.pageSize != null &&
        pagination.pageSize !== state.values.per_page
      ) {
        patch.per_page = pagination.pageSize
      }
      if (
        pagination.current != null &&
        pagination.current !== state.values.page &&
        // Only carry `page` through when nothing else changed; otherwise
        // setMany's auto-reset will land us on page 1 anyway.
        Object.keys(patch).length === 0
      ) {
        patch.page = pagination.current
      }
      if (Object.keys(patch).length > 0) {
        state.setMany(patch as Partial<ListValues<BaseSchema & Extra>>)
      }
    },
    [state]
  )

  return {
    ...state,
    buildPagination,
    onTableChange
  }
}
